/**
 * Police tuning shared by the single-player police (src/police/policeSystem.ts) and the server-run
 * units online (server/police/force.ts). Pure data, no engine or DOM imports: the server imports it.
 * Numbers and their reasons: docs/POLICE.md §3 and §11.
 */

/** Car units per wanted level (level 0 = patrols). */
export const CAR_CAP = [2, 2, 3, 4, 7] as const;
/** Foot officers per wanted level. */
export const FOOT_CAP = [0, 2, 3, 4, 6] as const;
/** Roadblocks (two cars each) per wanted level. */
export const ROADBLOCK_CAP = [0, 0, 0, 1, 2] as const;
/** Seconds between reinforcement spawns per level. */
export const SPAWN_INTERVAL = [14, 5, 3.5, 2.4, 1.7] as const;
/** Pursuit cruise speed as a fraction of the police car's top speed. */
export const PURSUIT_SPEED = [0.45, 0.6, 0.76, 0.85, 0.96] as const;
/**
 * OFFLINE ONLY (src/police/policeSystem.ts; the server-run units don't read it): how far short of a
 * suspect on foot a pursuing car pulls up (m), per level (was 10 at every level). At level 1 the crew
 * get out further back, so a sprinting suspect has the head start the foot-chase speeds (FOOT_RUN vs
 * the player's 6.6 m/s sprint) were tuned around. Level 2 stays at 10: measured further back it made
 * level-2 foot escapes rarer, not commoner (docs/POLICE.md §12).
 */
export const OFFLINE_FOOT_STANDOFF = [10, 17, 10, 10, 10] as const;
/** Sight range (m) of a crewed car per level, foot officer, roadblock. */
export const CAR_SIGHT = [42, 45, 50, 56, 62] as const;
export const FOOT_SIGHT = 30;
export const ROADBLOCK_SIGHT = 45;
/** Foot officer run speed per level (player sprints at 6.6 m/s: outrunnable with a head start). */
export const FOOT_RUN = [5.2, 5.4, 5.7, 6.0, 6.3] as const;
/**
 * Minimum spawn distance per level: high levels flood in closer (still never on screen), but always at
 * least 8 m beyond that level's car sight range, so a reinforcement has to drive into sight instead of
 * spawning already watching you.
 */
export const SPAWN_MIN_BY_LEVEL = [70, 70, 66, 64, 70] as const;
export const SPAWN_MAX_R = 145;
/** Seconds of standing still next to an officer on foot before the cuffs go on. */
export const BUST_TIME_FOOT = 1.1;
/** Seconds a stopped suspect in a car takes to arrest, per level: level 1 is forgiving. */
export const BUST_TIME_CAR = [3.2, 5.5, 4.5, 3.8, 3.2] as const;
/** Seconds the suspect's car must have been (nearly) stopped before crews get out, per level. */
export const DISMOUNT_DELAY = [0, 2.5, 1.8, 1.2, 0.8] as const;
/** Seconds between roadblock attempts once one was placed. */
export const ROADBLOCK_INTERVAL = 18;
/** A roadblock breaks up after this long (s). */
export const ROADBLOCK_LIFE = 55;
/** Armed response cars per level, only once firearms are authorised (a gun was used or shown at level 3+). */
export const ARMED_CARS = [0, 0, 0, 1, 2] as const;
/** Crimes that make it a firearms incident. */
export const FIREARM_CRIMES: ReadonlySet<string> = new Set(['firearmDischarge', 'shooting', 'brandishing']);
/** Armed officers draw on a suspect on foot within this range (and a clear line). */
export const ARMED_RANGE = 30;
/** Closer than this they go hands-on (an arrest) instead. */
export const ARMED_CLOSE = 4;
/** Aim wind-up before the first shot / later shots (s): the telegraph. */
export const ARMED_AIM_FIRST = 1.3;
export const ARMED_AIM = 0.9;
/** Per officer, and across all officers (s): never a volley. */
export const ARMED_SHOT_COOLDOWN = 2.4;
export const ARMED_SHOT_GAP = 0.8;
/** Health per hit (100 total, regenerates): it takes several hits to go down. */
export const ARMED_SHOT_DAMAGE = 14;
/** Hit chance before distance and movement: running targets are much harder to hit. */
export const ARMED_HIT_CHANCE = 0.55;

/** Hit chance of one police shot: distance (1 at ≤ 8 m, 0.45 at ≥ 30 m) and how fast the target moves. */
export function armedHitChance(distance: number, targetSpeed: number): number {
  const distK = Math.max(0.45, Math.min(1, 1 - (distance - 8) / 40));
  const moveK = targetSpeed > 5 ? 0.55 : targetSpeed > 2 ? 0.8 : 1;
  return ARMED_HIT_CHANCE * distK * moveK;
}

/*
 * Mayhem (docs/POLICE-AI-POLISH.md "Proposed escalation"): armed response comes with the level, not only with a
 * firearms incident, and armed cars shoot at a suspect's CAR (tyres first) from the passenger window.
 */
/** Armed cars are dispatched from this level on (earlier after a gun or a hard ram: ARMED_AFTER_RAM_LEVEL). */
export const ARMED_FROM_LEVEL = 3;
export const ARMED_AFTER_RAM_LEVEL = 2;
/** A ram of a unit at this closing speed (m/s) or more brings the armed cars out a level early. */
export const HARD_RAM_SPEED = 8;
/** Armed officers fire at the suspect's body (on foot) from this level, or a level earlier in a firearms incident. */
export const FIRE_AT_SUSPECT_LEVEL = 4;
/** From the passenger window: range (m) for a revolver and for the marksman's rifle, and the aim wind-up. */
export const CAR_SHOT_RANGE = 28;
export const MARKSMAN_RANGE = 40;
export const CAR_SHOT_COOLDOWN = 2.2;
/** Tyres are shot at from the level the armed cars arrive; the bodywork (and the driver through the glass) a level later. */
export const SHOOT_CAR_BODY_LEVEL = 4;
/** Damage a hit on the bodywork does to the car; a rifle round does more. */
export const CAR_SHOT_DAMAGE = 9;
export const MARKSMAN_CAR_DAMAGE = 18;
/** A round through the bodywork reaches the driver with this chance, for this much of their health. */
export const GLASS_HIT_CHANCE = 0.5;
export const GLASS_HIT_DAMAGE = 8;
export const MARKSMAN_GLASS_DAMAGE = 14;
/** Each punctured tyre takes this off the car's grip and top speed; at most PUNCTURE_MAX tyres. */
export const PUNCTURE_GRIP = 0.12;
export const PUNCTURE_TOP = 0.07;
export const PUNCTURE_MAX = 4;
/** A stinger (spike strip) at a level-4 roadblock punctures two tyres of a car crossing it faster than this (m/s). */
export const STINGER_LEVEL = 4;
export const STINGER_SPEED = 5;
export const STINGER_PUNCTURES = 2;
/** Police vans (four constables aboard) per level; they come once the whole borough is out. */
export const VAN_CAP = [0, 0, 0, 0, 1] as const;
export const VAN_CREW = 4;
export const VAN_SPEC = 'policevan';
/** A pursuing unit wrecked by your driving within this distance (m) is a takedown moment: a beat of slow motion. */
export const TAKEDOWN_RANGE = 35;
export const TAKEDOWN_SLOWMO_S = 0.45;

/**
 * Hit chance of a shot from a moving police car at a car (a tyre or the bodywork): distance as on foot, and a fast
 * target is hard to hit (0.5 above 15 m/s). Fair and readable: the first shots at a flat-out getaway mostly miss.
 */
export function carShotHitChance(distance: number, targetSpeed: number, rifle = false): number {
  const distK = Math.max(0.45, Math.min(1, 1 - (distance - 8) / (rifle ? 60 : 40)));
  const moveK = targetSpeed > 15 ? 0.5 : targetSpeed > 8 ? 0.65 : 0.85;
  return ARMED_HIT_CHANCE * distK * moveK * (rifle ? 1.25 : 1);
}
